// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { clearAuthTokens, getAccessToken, refreshAccessToken, setAccessToken, setRefreshToken } from 'api/authTokens';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadFromToken = (accessToken) => {
    try {
      const decoded = jwtDecode(accessToken);
      // console.log('DECODED TOKEN >>>', decoded);
      // { token_type, exp, iat, jti, user_id, role, username, email }

      const role = decoded.role || 'user';

      const authUser = {
        id: decoded.user_id,
        username: decoded.username,
        email: decoded.email,
        role,
        isAdmin: role === 'admin'
      };

      setToken(accessToken);
      setUser(authUser);
    } catch (e) {
      console.error('Invalid token', e);
      setAccessToken(null);
      setToken(null);
      setUser(null);
    }
  };

  const login = (accessToken, refreshToken = null) => {
    // call this after successful POST /api/auth/token/
    setAccessToken(accessToken); // persist (localStorage, etc.)
    if (refreshToken) setRefreshToken(refreshToken);
    loadFromToken(accessToken);
  };

  const logout = () => {
    clearAuthTokens();
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    // An expired access token is swapped for a new one with the refresh token; if that fails the visitor is signed out.
    const start = async () => {
      let existing = getAccessToken();
      if (existing) {
        let expired = false;
        try { expired = jwtDecode(existing).exp * 1000 <= Date.now(); } catch { expired = true; }
        if (expired) existing = await refreshAccessToken();
        if (existing) loadFromToken(existing); else clearAuthTokens();
      }
      setLoading(false);
    };
    start();
  }, []);

  const role = user?.role || 'user';
  const isAdmin = user?.isAdmin || false;

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        role,
        isAdmin,
        login,
        logout,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
